"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NexusExtension = void 0;
const NexusAutoWatcher_1 = require("./NexusAutoWatcher");
/**
 * Nexus扩展管理器
 * 用于集成到Cocos Creator的扩展系统
 */
class NexusExtension {
    constructor() {
        this.plugins = new Map();
        this.panels = new Map();
    }
    static get Instance() {
        if (!NexusExtension.instance) {
            NexusExtension.instance = new NexusExtension();
        }
        return NexusExtension.instance;
    }
    /**
     * 注册插件
     */
    registerPlugin(plugin) {
        const name = plugin.getName();
        if (this.plugins.has(name)) {
            console.warn(`Plugin ${name} is already registered`);
            return;
        }
        this.plugins.set(name, plugin);
        plugin.onLoad();
        console.log(`Plugin ${name} registered successfully`);
    }
    /**
     * 注册面板
     */
    registerPanel(name, panel) {
        if (this.panels.has(name)) {
            console.warn(`Panel ${name} is already registered`);
            return;
        }
        this.panels.set(name, panel);
        console.log(`Panel ${name} registered successfully`);
    }
    /**
     * 获取插件
     */
    getPlugin(name) {
        return this.plugins.get(name);
    }
    /**
     * 获取面板
     */
    getPanel(name) {
        return this.panels.get(name);
    }
    /**
     * 获取所有插件
     */
    getAllPlugins() {
        return Array.from(this.plugins.values());
    }
    /**
     * 获取所有面板
     */
    getAllPanels() {
        return Array.from(this.panels.values());
    }
    /**
     * 卸载插件
     */
    unregisterPlugin(name) {
        const plugin = this.plugins.get(name);
        if (plugin) {
            plugin.onUnload();
            this.plugins.delete(name);
            console.log(`Plugin ${name} unregistered successfully`);
        }
    }
    /**
     * 卸载面板
     */
    unregisterPanel(name) {
        const panel = this.panels.get(name);
        if (panel) {
            panel.onDestroy();
            this.panels.delete(name);
            console.log(`Panel ${name} unregistered successfully`);
        }
    }
    /**
     * 启动扩展系统
     */
    static async start() {
        console.log('🚀 启动 Nexus 扩展系统...');
        try {
            // 启动自动文件监听器
            console.log('📡 启动自动文件监听器...');
            NexusAutoWatcher_1.AutoWatcher.start();
            console.log('✅ Nexus 扩展系统已启动');
            console.log('🎉 零配置开发体验已激活 - 无需手动运行watch命令！');
        }
        catch (error) {
            console.error('❌ 启动扩展系统失败:', error);
            throw error;
        }
    }
    /**
     * 停止扩展系统
     */
    static async stop() {
        console.log('🛑 停止 Nexus 扩展系统...');
        try {
            // 停止自动文件监听器
            NexusAutoWatcher_1.AutoWatcher.stop();
            console.log('✅ Nexus 扩展系统已停止');
        }
        catch (error) {
            console.error('❌ 停止扩展系统失败:', error);
            throw error;
        }
    }
    /**
     * 创建标准的Cocos面板定义
     * 这是核心功能：将Nexus面板转换为Cocos面板
     */
    createCocosPanel(nexusPanel, panelName) {
        const config = nexusPanel.getConfig();
        // 生成面板HTML模板
        const template = `
            <div id="nexus-panel-${panelName}" style="width: 100%; height: 100%; overflow: auto;">
                <div id="nexus-content-${panelName}" style="padding: 10px; height: 100%; box-sizing: border-box;"></div>
            </div>
        `;
        // 生成面板CSS样式
        const style = this.generatePanelStyles(panelName);
        // 生成面板选择器
        const $ = {
            content: `#nexus-content-${panelName}`
        };
        // 生成面板方法
        const methods = {
            ready() {
                console.log(`Nexus面板 ${panelName} 准备就绪`);
                // 初始化面板
                nexusPanel.onInit();
                // 设置容器
                const container = document.querySelector(`#nexus-content-${panelName}`);
                if (container) {
                    nexusPanel.setContainer(container);
                    // 开始渲染循环
                    const renderLoop = () => {
                        if (nexusPanel) {
                            nexusPanel.render();
                        }
                        requestAnimationFrame(renderLoop);
                    };
                    renderLoop();
                }
            },
            close() {
                console.log(`Nexus面板 ${panelName} 关闭`);
                nexusPanel.onDestroy();
            }
        };
        // 返回完整的面板定义
        const panelDefinition = {
            template,
            style,
            $,
            methods
        };
        return panelDefinition;
    }
    /**
     * 生成面板样式
     */
    generatePanelStyles(panelName) {
        return `
            #nexus-panel-${panelName} {
                background-color: #f0f0f0;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                font-size: 14px;
                color: #333;
            }

            .nexus-label {
                margin: 4px 0;
                padding: 2px 4px;
                display: block;
            }

            .nexus-button {
                margin: 2px;
                padding: 6px 12px;
                border: 1px solid #d0d0d0;
                border-radius: 4px;
                background-color: #fff;
                cursor: pointer;
                font-size: 14px;
                transition: all 0.2s;
            }

            .nexus-button:hover {
                background-color: #e8e8e8;
                border-color: #b0b0b0;
            }

            .nexus-button:active {
                background-color: #d0d0d0;
                transform: translateY(1px);
            }

            .nexus-textfield {
                margin: 2px;
                padding: 6px 8px;
                border: 1px solid #d0d0d0;
                border-radius: 4px;
                font-size: 14px;
                width: 100%;
                box-sizing: border-box;
            }

            .nexus-textfield:focus {
                outline: none;
                border-color: #007acc;
                box-shadow: 0 0 0 2px rgba(0, 122, 204, 0.2);
            }

            .nexus-toggle-container {
                display: flex;
                align-items: center;
                margin: 4px 0;
            }

            .nexus-toggle {
                margin-right: 8px;
            }

            .nexus-dropdown {
                margin: 2px;
                padding: 6px 8px;
                border: 1px solid #d0d0d0;
                border-radius: 4px;
                font-size: 14px;
                background-color: #fff;
                cursor: pointer;
            }

            .nexus-slider {
                margin: 4px 0;
                width: 100%;
            }

            .nexus-separator {
                margin: 8px 0;
                height: 1px;
                background-color: #e0e0e0;
            }

            .nexus-tab-group {
                display: flex;
                margin: 4px 0;
                border-bottom: 1px solid #e0e0e0;
            }

            .nexus-tab {
                padding: 8px 16px;
                cursor: pointer;
                border: none;
                background: none;
                font-size: 14px;
                transition: all 0.2s;
            }

            .nexus-tab:hover {
                background-color: #f0f0f0;
            }

            .nexus-tab.active {
                background-color: #007acc;
                color: white;
            }
        `;
    }
    /**
     * 生成Cocos Creator扩展的main.ts
     */
    generateMainTs() {
        const methods = [];
        const imports = [];
        // 生成面板相关方法
        this.panels.forEach((panel, name) => {
            const methodName = `open${name.charAt(0).toUpperCase() + name.slice(1)}`;
            methods.push(`
    '${methodName}'() {
        Editor.Panel.open('${name}');
    }`);
        });
        // 生成插件相关方法
        this.plugins.forEach((plugin) => {
            const config = plugin.getConfig();
            if (config.menu) {
                config.menu.forEach((menu) => {
                    methods.push(`
    '${menu.action}'() {
        // 插件方法实现
        console.log('执行插件方法: ${menu.action}');
    }`);
                });
            }
        });
        return `
import { NexusExtension } from '@esengine/cocos-nexus';
${imports.join('\n')}

export const methods = {${methods.join(',\n')}
};

export function load() {
    console.log('Nexus Extension loaded');
}

export function unload() {
    console.log('Nexus Extension unloaded');
}
`;
    }
    /**
     * 生成面板文件内容
     * 这是关键功能：自动生成符合Cocos标准的面板文件
     */
    generatePanelFile(nexusPanel, panelName) {
        const className = nexusPanel.constructor.name;
        return `
import { ${className} } from '../${className}';

let panel: ${className} | null = null;

const template = \`
<div id="nexus-panel-${panelName}" style="width: 100%; height: 100%; overflow: auto;">
    <div id="nexus-content-${panelName}" style="padding: 10px; height: 100%; box-sizing: border-box;"></div>
</div>
\`;

const style = \`
${this.generatePanelStyles(panelName)}
\`;

const $ = {
    content: '#nexus-content-${panelName}'
};

const methods = {
    ready() {
        console.log('Nexus面板 ${panelName} 准备就绪');
        
        try {
            // 创建面板实例
            panel = new ${className}({
                title: "${nexusPanel.getConfig().title}",
                width: ${nexusPanel.getConfig().width},
                height: ${nexusPanel.getConfig().height},
                dockable: ${nexusPanel.getConfig().dockable},
                resizable: ${nexusPanel.getConfig().resizable}
            });

            // 初始化面板
            panel.onInit();
            
            // 设置容器
            const container = document.querySelector('#nexus-content-${panelName}') as HTMLElement;
            if (container) {
                panel.setContainer(container);
                
                // 开始渲染循环
                const renderLoop = () => {
                    if (panel) {
                        panel.render();
                    }
                    requestAnimationFrame(renderLoop);
                };
                renderLoop();
            }
        } catch (error) {
            console.error('面板初始化失败:', error);
        }
    },

    close() {
        console.log('Nexus面板 ${panelName} 关闭');
        try {
            if (panel) {
                panel.onDestroy();
                panel = null;
            }
        } catch (error) {
            console.error('面板关闭失败:', error);
        }
    }
};

// 使用正确的 Editor.Panel.define() 格式导出面板定义
module.exports = Editor.Panel.define({
    template,
    style,
    $,
    methods
});
`;
    }
    /**
     * 自动化插件集成
     * 这是你需要的核心功能：让插件遵循你的规范
     */
    integratePlugin(plugin) {
        const config = plugin.getConfig();
        const result = {
            mainTs: '',
            panelFiles: new Map(),
            packageJson: {}
        };
        // 生成main.ts
        result.mainTs = this.generatePluginMainTs(plugin);
        // 生成面板文件
        if (config.panels) {
            Object.entries(config.panels).forEach(([panelName, panelConfig]) => {
                const panel = this.panels.get(panelName);
                if (panel) {
                    result.panelFiles.set(panelName, this.generatePanelFile(panel, panelName));
                }
            });
        }
        // 生成package.json配置
        result.packageJson = this.generatePackageJson(plugin);
        return result;
    }
    /**
     * 生成插件的main.ts文件
     */
    generatePluginMainTs(plugin) {
        const config = plugin.getConfig();
        const className = plugin.constructor.name;
        const methods = [];
        // 生成菜单方法
        if (config.menu) {
            config.menu.forEach((menu) => {
                methods.push(`
    '${menu.action}'() {
        if (pluginInstance) {
            // 调用插件方法
            console.log('执行插件方法: ${menu.action}');
        }
    }`);
            });
        }
        // 生成面板打开方法
        if (config.panels) {
            Object.keys(config.panels).forEach((panelName) => {
                const methodName = `open-${panelName}-panel`;
                methods.push(`
    '${methodName}'() {
        Editor.Panel.open('${config.name}.${panelName}');
    }`);
            });
        }
        return `
import { ${className} } from './${className}';

let pluginInstance: ${className} | null = null;

export const methods = {${methods.join(',')}
};

export function load() {
    console.log('${config.name} 插件加载');
    pluginInstance = new ${className}();
}

export function unload() {
    console.log('${config.name} 插件卸载');
    if (pluginInstance) {
        pluginInstance.onUnload();
        pluginInstance = null;
    }
}
`;
    }
    /**
     * 生成package.json配置
     */
    generatePackageJson(plugin) {
        const config = plugin.getConfig();
        const packageJson = {
            name: config.name,
            package_version: 2,
            version: config.version,
            description: config.description,
            author: config.author,
            editor: ">=3.8.0",
            main: "./dist/main.js",
            panels: {},
            contributions: {
                menu: [],
                messages: {}
            }
        };
        // 生成菜单配置
        if (config.menu) {
            packageJson.contributions.menu = config.menu.map(menu => ({
                path: menu.path,
                label: menu.label,
                message: menu.action
            }));
            // 生成消息配置
            config.menu.forEach(menu => {
                packageJson.contributions.messages[menu.action] = {
                    methods: [menu.action]
                };
            });
        }
        // 生成面板配置
        if (config.panels) {
            Object.entries(config.panels).forEach(([panelName, panelConfig]) => {
                packageJson.panels[panelName] = {
                    title: panelConfig.title,
                    type: panelConfig.dockable !== false ? "dockable" : "simple",
                    main: `./dist/${panelName}`,
                    size: {
                        "min-width": Math.max(300, panelConfig.minWidth || 300),
                        "min-height": Math.max(200, panelConfig.minHeight || 200),
                        "width": panelConfig.width || 600,
                        "height": panelConfig.height || 400
                    }
                };
            });
        }
        return packageJson;
    }
}
exports.NexusExtension = NexusExtension;
